import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "../../design-system/icons";
import { typography, surfaces, borders, textColors, Z_INDEX } from "../../design-system/tokens";
import {
  COACHMARK_DEFAULT_WIDTH_PX,
  COACHMARK_GAP_PX,
  DEFAULT_HIGHLIGHT,
  VIEWPORT_PADDING_PX,
} from "./constants";
import { computeCoachmarkPosition, expandRect, toPlainRect } from "./positioning";
import { useEditorTutorial } from "./useEditorTutorial";

const isOverflowScrollable = (value) => {
  const v = String(value || "").toLowerCase();
  return v === "auto" || v === "scroll" || v === "overlay";
};

const getScrollParents = (el) => {
  const parents = [];
  if (!el || typeof window === "undefined") return parents;
  let cur = el.parentElement;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    try {
      const style = window.getComputedStyle(cur);
      if (isOverflowScrollable(style.overflowY) || isOverflowScrollable(style.overflowX)) {
        parents.push(cur);
      }
    } catch (_) {}
    cur = cur.parentElement;
  }
  return parents;
};

const buildTutorialSelector = (targetId) => {
  if (!targetId) return null;
  // Keep this resilient; target ids are controlled by us but don't risk selector injection.
  const safe = String(targetId).replace(/"/g, '\\"');
  return `[data-tutorial-id="${safe}"]`;
};

const getElementForTargetId = (targetId) => {
  const sel = buildTutorialSelector(targetId);
  if (!sel) return null;
  try {
    return document.querySelector(sel);
  } catch (_) {
    return null;
  }
};

const IconNavButton = ({ onClick, disabled, ariaLabel, children, glow = false, reduceMotion = false }) => {
  const shouldGlow = glow && !disabled;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={[
        "w-9 h-9 inline-flex items-center justify-center",
        "rounded-md border",
        borders.default,
        surfaces.tertiary,
        textColors.primary,
        surfaces.hover,
        "transition-colors transition-shadow",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400",
        disabled ? "opacity-50 cursor-not-allowed" : "",
      ].join(" ")}
      style={
        shouldGlow
          ? {
              boxShadow:
                "0 0 0 1px rgba(96,165,250,0.32), 0 0 10px 2px rgba(59,130,246,0.24), 0 8px 16px -10px rgba(37,99,235,0.45)",
            }
          : undefined
      }
      animate={
        shouldGlow && !reduceMotion
          ? {
              boxShadow: [
                "0 0 0 1px rgba(96,165,250,0.32), 0 0 10px 2px rgba(59,130,246,0.24), 0 8px 16px -10px rgba(37,99,235,0.45)",
                "0 0 0 2px rgba(147,197,253,0.42), 0 0 14px 3px rgba(59,130,246,0.34), 0 10px 18px -10px rgba(37,99,235,0.5)",
                "0 0 0 1px rgba(96,165,250,0.32), 0 0 10px 2px rgba(59,130,246,0.24), 0 8px 16px -10px rgba(37,99,235,0.45)",
              ],
            }
          : undefined
      }
      transition={
        shouldGlow && !reduceMotion
          ? {
              duration: 1.8,
              ease: "easeInOut",
              repeat: Infinity,
            }
          : undefined
      }
    >
      {children}
    </motion.button>
  );
};

const CloseButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Close tutorial"
    className={[
      "p-1.5 rounded-md border",
      borders.default,
      surfaces.tertiary,
      textColors.primary,
      surfaces.hover,
      "transition-colors",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400",
    ].join(" ")}
  >
    <XIcon size="sm" />
  </button>
);

const CoachmarkArrow = ({ placement }) => {
  if (!placement || placement === "center") return null;
  const size = 12;
  const half = size / 2;

  const base = {
    width: size,
    height: size,
    transform: "rotate(45deg)",
  };

  const pos = (() => {
    switch (placement) {
      case "right":
        return { left: -half, top: "50%", transform: "translateY(-50%) rotate(45deg)" };
      case "left":
        return { right: -half, top: "50%", transform: "translateY(-50%) rotate(45deg)" };
      case "bottom":
        return { top: -half, left: "50%", transform: "translateX(-50%) rotate(45deg)" };
      case "top":
        return { bottom: -half, left: "50%", transform: "translateX(-50%) rotate(45deg)" };
      default:
        return null;
    }
  })();

  if (!pos) return null;

  return (
    <div
      aria-hidden="true"
      className={["absolute border", surfaces.elevated, borders.default].join(" ")}
      style={{
        ...base,
        ...pos,
      }}
    />
  );
};

const EditorTutorialOverlay = () => {
  const {
    isOpen,
    steps,
    stepIndex,
    currentStep,
    canGoPrev,
    canGoNext,
    prev,
    next,
    close,
  } = useEditorTutorial();

  const reduceMotion = useReducedMotion();

  const coachmarkRef = useRef(null);
  const rafIdRef = useRef(null);
  const targetElRef = useRef(null);

  const [coachmarkSize, setCoachmarkSize] = useState({
    width: COACHMARK_DEFAULT_WIDTH_PX,
    height: 180,
  });
  const [targetRect, setTargetRect] = useState(null);

  const updateTargetRect = useCallback(() => {
    if (!isOpen) return;
    const targetId = currentStep?.targetId;
    if (!targetId) {
      targetElRef.current = null;
      setTargetRect(null);
      return;
    }
    const el = getElementForTargetId(targetId);
    targetElRef.current = el;
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = toPlainRect(el.getBoundingClientRect());
    setTargetRect(rect);
  }, [currentStep?.targetId, isOpen]);

  const scheduleUpdate = useCallback(() => {
    if (!isOpen) return;
    if (rafIdRef.current) return;
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      updateTargetRect();
    });
  }, [isOpen, updateTargetRect]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateTargetRect();
    return undefined;
  }, [isOpen, stepIndex, updateTargetRect]);

  // Keep highlight/coachmark aligned on resize.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [isOpen, scheduleUpdate]);

  // Also listen to scroll on relevant scroll parents for the current target.
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = targetElRef.current;
    if (!el) return undefined;

    const parents = getScrollParents(el);
    parents.forEach((p) => {
      try {
        p.addEventListener("scroll", scheduleUpdate, { passive: true });
      } catch (_) {}
    });

    // Always include window scroll (for document scrolling).
    window.addEventListener("scroll", scheduleUpdate, { passive: true });

    return () => {
      parents.forEach((p) => {
        try {
          p.removeEventListener("scroll", scheduleUpdate);
        } catch (_) {}
      });
      window.removeEventListener("scroll", scheduleUpdate);
    };
  }, [isOpen, scheduleUpdate, stepIndex]);

  // Observe target size changes so the highlight stays accurate even without scroll/resize.
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = targetElRef.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === "undefined") return undefined;

    const ro = new ResizeObserver(() => scheduleUpdate());
    try {
      ro.observe(el);
    } catch (_) {}
    return () => {
      try {
        ro.disconnect();
      } catch (_) {}
    };
  }, [isOpen, scheduleUpdate, stepIndex]);

  // Watch DOM mutations while tutorial is open so targets that mount later can be detected.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof MutationObserver === "undefined") return undefined;
    if (!document?.body) return undefined;

    const mo = new MutationObserver(() => scheduleUpdate());
    try {
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (_) {}

    return () => {
      try {
        mo.disconnect();
      } catch (_) {}
    };
  }, [isOpen, scheduleUpdate]);

  // Measure coachmark to anchor it accurately near the target.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const el = coachmarkRef.current;
    if (!el) return undefined;

    const measure = () => {
      const r = el.getBoundingClientRect();
      if (!r?.width || !r?.height) return;
      setCoachmarkSize((prev) => {
        if (prev.width === r.width && prev.height === r.height) return prev;
        return { width: r.width, height: r.height };
      });
    };

    measure();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    return () => {
      if (ro) ro.disconnect();
    };
  }, [isOpen, stepIndex]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        try {
          window.cancelAnimationFrame(rafIdRef.current);
        } catch (_) {}
        rafIdRef.current = null;
      }
    };
  }, []);

  const highlight = currentStep?.highlight || DEFAULT_HIGHLIGHT;
  const highlightRect = useMemo(() => {
    const pad = highlight?.paddingPx ?? DEFAULT_HIGHLIGHT.paddingPx;
    return targetRect ? expandRect(targetRect, pad) : null;
  }, [highlight?.paddingPx, targetRect]);
  const highlightRadius = highlight?.radiusPx ?? DEFAULT_HIGHLIGHT.radiusPx;

  const placement = currentStep?.placement || "auto";

  const coachmarkPos = useMemo(() => {
    return computeCoachmarkPosition({
      targetRect: highlightRect,
      coachmarkSize,
      placement,
      gapPx: COACHMARK_GAP_PX,
      viewportPaddingPx: VIEWPORT_PADDING_PX,
    });
  }, [coachmarkSize, highlightRect, placement]);

  const showNav = currentStep?.showNav !== false;
  const isLastStep = stepIndex >= Math.max((steps?.length || 1) - 1, 0);

  if (!isOpen || !currentStep) return null;
  if (typeof document === "undefined") return null;

  const titleId = `editor-tutorial-title-${currentStep.id}`;
  const bodyId = `editor-tutorial-body-${currentStep.id}`;

  return createPortal(
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: Z_INDEX.CRITICAL }}
    >
      {/* Highlight */}
      {highlightRect ? (
        <div
          className="fixed pointer-events-none border-2 border-blue-600 dark:border-blue-400"
          aria-hidden="true"
          style={{
            left: `${highlightRect.left}px`,
            top: `${highlightRect.top}px`,
            width: `${highlightRect.width}px`,
            height: `${highlightRect.height}px`,
            borderRadius: `${highlightRadius}px`,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
            transition: "left 180ms ease-out, top 180ms ease-out, width 180ms ease-out, height 180ms ease-out",
            zIndex: 1,
          }}
        />
      ) : null}

      {/* Coachmark */}
      <motion.div
        ref={coachmarkRef}
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className={[
          "fixed pointer-events-auto",
          "border rounded-xl",
          surfaces.elevated,
          borders.default,
        ].join(" ")}
        style={{
          left: `${coachmarkPos.left}px`,
          top: `${coachmarkPos.top}px`,
          width: `${COACHMARK_DEFAULT_WIDTH_PX}px`,
          maxWidth: "calc(100vw - 24px)",
          transition: "left 180ms ease-out, top 180ms ease-out",
          zIndex: 2,
        }}
        animate={
          reduceMotion
            ? undefined
            : {
                boxShadow: [
                  "0 0 0 2px rgba(96,165,250,0.34), 0 0 26px 10px rgba(59,130,246,0.34), 0 0 84px 26px rgba(37,99,235,0.26), 0 25px 50px -12px rgba(0,0,0,0.25)",
                  "0 0 0 3px rgba(147,197,253,0.48), 0 0 34px 12px rgba(59,130,246,0.42), 0 0 104px 34px rgba(37,99,235,0.32), 0 25px 50px -12px rgba(0,0,0,0.25)",
                  "0 0 0 2px rgba(96,165,250,0.34), 0 0 26px 10px rgba(59,130,246,0.34), 0 0 84px 26px rgba(37,99,235,0.26), 0 25px 50px -12px rgba(0,0,0,0.25)",
                ],
              }
        }
        transition={
          reduceMotion
            ? undefined
            : {
                duration: 2.4,
                ease: "easeInOut",
                repeat: Infinity,
              }
        }
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <CoachmarkArrow placement={coachmarkPos.placement} />
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div id={titleId} className={`${typography.h5} ${textColors.primary}`}>
                {currentStep.title}
              </div>
            </div>
            <CloseButton onClick={close} />
          </div>

          <div id={bodyId} className={`mt-2 ${typography.bodySmall} ${textColors.secondary}`}>
            {currentStep.body}
          </div>

          {showNav ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <div className={`${typography.caption} ${textColors.muted}`}>
                Step {stepIndex + 1} of {steps.length}
              </div>
              <div className="flex items-center gap-2">
                <IconNavButton
                  onClick={prev}
                  disabled={!canGoPrev}
                  ariaLabel="Previous step"
                  reduceMotion={reduceMotion}
                >
                  <ChevronLeftIcon size="sm" />
                </IconNavButton>
                <IconNavButton
                  onClick={isLastStep ? close : next}
                  disabled={!canGoNext && !isLastStep}
                  ariaLabel={isLastStep ? "Finish tutorial" : "Next step"}
                  glow
                  reduceMotion={reduceMotion}
                >
                  <ChevronRightIcon size="sm" />
                </IconNavButton>
              </div>
            </div>
          ) : (
            <div className={`mt-4 ${typography.caption} ${textColors.muted}`}>
              Waiting for your click…
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

export default EditorTutorialOverlay;

